// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';

// components
import { formatHour } from '@/utils/formatters';
const ORDER = [
  { key: 'monday', label: 'Mandag', shortLabel: 'M' },
  { key: 'tuesday', label: 'Tirsdag', shortLabel: 'T' },
  { key: 'wednesday', label: 'Onsdag', shortLabel: 'O' },
  { key: 'thursday', label: 'Torsdag', shortLabel: 'T' },
  { key: 'friday', label: 'Fredag', shortLabel: 'F' },
  { key: 'saturday', label: 'Lørdag', shortLabel: 'L' },
  { key: 'sunday', label: 'Søndag', shortLabel: 'S' },
];
/**
 * Component that displays active or inactive hours for each day of the week
 * @param {Object} props
 * @param {Array} props.items - Array of items with {key, label, intervals}
 * @param {Function} props.formatHour - Function to format hour numbers
 * @param {Function} props.clamp - Function to clamp values
 * @param {boolean} props.inverted - If true, shows inactive hours instead of active hours
 */
export default function ActiveHoursTooltip({ openingHours, inverted = false }) {
  const theme = useTheme();

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const { t } = useTranslation('energy-analysis');

  if (!openingHours) {
    return (
      <Typography variant="body2">
        {t('no_consumption_pattern_found')}
      </Typography>
    );
  }

  const items = ORDER.map(({ key, label }) => ({
    key,
    label,
    intervals: Array.isArray(openingHours[key]) ? openingHours[key] : [],
  }));
  // Build all 24-hour segments for a single bar
  const buildTimelineSegments = (intervals) => {
    if (intervals.length === 0) {
      // No active hours - entire day is one segment
      return [{ start: 0, end: 24, isActive: false }];
    }

    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const segments = [];
    let currentTime = 0;

    sorted.forEach((interval) => {
      const start = clamp(interval.start, 0, 24);
      const end = clamp(Math.max(interval.end, interval.start), 0, 24);

      // Add inactive segment before this interval
      if (currentTime < start) {
        segments.push({ start: currentTime, end: start, isActive: false });
      }

      // Add active segment
      if (start < end) {
        segments.push({ start, end, isActive: true });
        currentTime = end;
      }
    });

    // Add remaining inactive segment if any
    if (currentTime < 24) {
      segments.push({ start: currentTime, end: 24, isActive: false });
    }

    return segments;
  };

  return (
    <Stack divider={<Divider flexItem />} spacing={0} sx={{ minWidth: 200 }}>
      {items.map(({ key, label, intervals }) => {
        const segments = buildTimelineSegments(intervals);
        const singleLetter = label?.charAt(0) || '';

        // Get all transition times (where color changes)
        const transitionTimes = new Set();
        segments.forEach((segment) => {
          if (segment.start > 0) transitionTimes.add(segment.start);
          if (segment.end < 24) transitionTimes.add(segment.end);
        });
        const sortedTimes = Array.from(transitionTimes).sort((a, b) => a - b);

        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
            }}
          >
            <Typography
              sx={{ minWidth: 20, fontWeight: 500, textAlign: 'center' }}
            >
              {singleLetter}
            </Typography>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              {/* Single timeline bar */}
              <Box
                sx={{
                  position: 'relative',
                  height: 16,
                  width: '100%',
                  borderRadius: 8,
                  overflow: 'hidden',
                  display: 'flex',
                  background: theme.palette.grey[700],
                }}
              >
                {segments.map((segment, idx) => {
                  const widthPercent =
                    ((segment.end - segment.start) / 24) * 100;
                  const shouldHighlight = inverted
                    ? !segment.isActive
                    : segment.isActive;

                  return (
                    <Box
                      key={`${key}-segment-${idx}`}
                      sx={{
                        width: `${widthPercent}%`,
                        height: '100%',
                        background: shouldHighlight
                          ? theme.palette.grey[200]
                          : 'transparent',
                      }}
                    />
                  );
                })}
              </Box>

              {/* Time labels at color transitions */}
              {sortedTimes.length > 0 && (
                <Box
                  sx={{
                    position: 'relative',
                    height: 16,
                    width: '100%',
                  }}
                >
                  {sortedTimes.map((time) => (
                    <Typography
                      variant="body2"
                      key={time}
                      sx={{
                        position: 'absolute',
                        left: `${(time / 24) * 100}%`,
                        transform: 'translateX(-50%)',
                        fontSize: '0.65rem',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatHour(time)}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
